<script setup lang="ts">
import { computed } from 'vue';
import { formatCurrency } from '@/utils/currency';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { type PaymentSummary, type ShiftSummary } from '@/api/payments';

interface Props {
  title: string;
  paymentsSummary: PaymentSummary[];
  shiftsCount: number;
  shiftsCountWithTaxes: number;
  cost: number;
  taxes: number;
  totalCost: number;
}

const props = defineProps<Props>();

const { tableProps } = useResponsiveTable();

interface CaregiverDetailRow {
  patient: string;
  shiftType: string;
  shiftsCount: number;
  cost: number;
  taxes: number;
  totalNetCost: number;
  totalNetTaxes: number;
  totalGrossAmount: number;
}

const rows = computed(() => {
  const result: CaregiverDetailRow[] = [];
  props.paymentsSummary.forEach((payment: PaymentSummary) => {
    payment.shiftsSummary.forEach((shifts: ShiftSummary) => {
      result.push({
        patient: payment.patient.fullName,
        shiftType: payment.shiftType,
        ...shifts,
      });
    });
  });
  return result;
});

const headers = [
  { title: 'Paciente', value: 'patient' },
  { title: 'Tipo de Turno', value: 'shiftType' },
  { title: 'Cantidad de Turnos', value: 'shiftsCount' },
  { title: 'Cantidad de Turnos con Honorarios', value: 'shiftsCountWithTaxes' },
  { title: 'Pago por Turno', value: 'cost' },
  { title: 'Monto Bruto', value: 'totalGrossAmount' },
  { title: 'Retención Impuestos', value: 'totalNetTaxes' },
  { title: 'Pago a Profesional', value: 'totalNetCost' },
];

function shiftTypeText(shiftType: string) {
  if (shiftType === 'weekday') return 'Semana';
  if (shiftType === 'weekend') return 'Fin de Semana';
  if (shiftType === 'holiday') return 'Feriado';
  if (shiftType === 'inalienable_holiday') return 'Feriado Irrenunciable';
  return '';
}
</script>

<template>
  <v-expansion-panel>
    <v-expansion-panel-title class="font-weight-bold">
      {{ title }}
      <v-spacer />
      <v-chip
        color="green-darken-3"
        class="mr-8"
      >
        {{ formatCurrency(cost) }}
      </v-chip>
    </v-expansion-panel-title>
    <v-expansion-panel-text>
      <v-data-table
        v-bind="tableProps"
        :headers="headers"
        :header-props="{ class: 'font-weight-bold' }"
        :items="rows"
        class="elevation-1"
      >
        <template #item.shiftType="{ item }">
          {{ shiftTypeText(item.shiftType) }}
        </template>
        <template #item.cost="{ item }">
          {{ formatCurrency(item.cost) }}
        </template>
        <template #item.taxes="{ item }">
          {{ formatCurrency(item.taxes) }}
        </template>
        <template #item.totalGrossAmount="{ item }">
          {{ formatCurrency(item.totalGrossAmount) }}
        </template>
        <template #item.totalNetTaxes="{ item }">
          {{ formatCurrency(item.totalNetTaxes) }}
        </template>
        <template #item.totalNetCost="{ item }">
          <v-chip color="primary">
            {{ formatCurrency(item.totalNetCost) }}
          </v-chip>
        </template>
        <template #body.append>
          <tr>
            <th>-</th>
            <th>-</th>
            <th class="font-weight-bold">
              {{ shiftsCount }}
            </th>
            <th class="font-weight-bold">
              {{ shiftsCountWithTaxes }}
            </th>
            <th>-</th>
            <th class="font-weight-bold">
              {{ formatCurrency(totalCost) }}
            </th>
            <th class="font-weight-bold">
              {{ formatCurrency(taxes) }}
            </th>
            <th>
              <v-chip color="green-darken-3">
                {{ formatCurrency(cost) }}
              </v-chip>
            </th>
          </tr>
        </template>
      </v-data-table>
    </v-expansion-panel-text>
  </v-expansion-panel>
</template>
